import { invalidateExploreSummary } from '../../../explore/exploreRefresh';
import AlbumDetailHeader from './AlbumDetailHeader';
import React, { useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import StableDialog from '../../../../components/common/StableDialog';
import { DialogTitle, DialogContent } from '@mui/material';
import { FiBookmark, FiEdit, FiList, FiPlus, FiUpload, FiX, FiChevronRight } from 'react-icons/fi';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useWebLocale } from '../../../../contexts/WebLocaleContext';
import { useUserStore } from '../../../../stores/userStore';
import { saveCurrentPathAndRedirectToLogin } from '../../../../utils/authUtils';
import { albumSharePath } from '../../../../utils/albumSharePath';
import { shareOrCopyUrl } from '../../../../utils/share';
import { trackShareClick, trackReviewCreate } from '../../../../lib/gaEvents';
import { CONTENT_TYPE } from '../../../../constants/gaEnums';
import { meApi } from '../../../../api/me';
import { playlistApi } from '../../../../api/playlist';
import { recentScope, removeRecent } from '../../../../features/recent/recentContentStore';
import { albumPlaylistSeed, sameAlbumSource } from '../../../playlist/albumPlaylistSeed';
import { ownAlbumReview, saveAlbumReview, deleteAlbumReview } from './albumActionModel';
import AlbumActionEditor, { RatingStar } from './AlbumActionEditor';
import AlbumActionConfirm from './AlbumActionConfirm';
import './AlbumPrimaryActions.css';

const AlbumPrimaryActions = React.forwardRef(function AlbumPrimaryActions(props, ref) {
  const user = useUserStore((state) => state.user);
  // Private drafts and playlist queries cannot survive account/album changes.
  return (
    <Actions
      key={`${user?.id ?? 'guest'}:${props.album.albumSource}:${props.album.sourceId}`}
      {...props}
      actionRef={ref}
      user={user}
    />
  );
});
export default AlbumPrimaryActions;

function Actions({
  album,
  reviews = [],
  user,
  onBookmarkToggle,
  bookmarkPending,
  pageType,
  actionRef,
  headerOptions,
}) {
  const { locale } = useWebLocale();
  const en = locale === 'en';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState(null);
  const reviewInput = useRef(null);
  const openReview = () => {
    // Safari requires focus in the original tap, before the sheet transition ends.
    flushSync(() => setMode('review'));
    reviewInput.current?.focus({ preventScroll: true });
  };
  const [message, setMessage] = useState('');
  const [bookmarkOverride, setBookmarkOverride] = useState(null);
  const bookmarkBusy = useRef(false);
  const [bookmarkWorking, setBookmarkWorking] = useState(false);
  const review = ownAlbumReview(album, reviews, user?.id);
  const rated = Boolean(review?.id) || (Boolean(user) && Number(album.userRating) > 0);
  const reviewed = Boolean(review?.content?.trim());
  const saved = bookmarkOverride ?? Boolean(user && album.isBookmarked);
  const requireLogin = (action) => (user ? action() : saveCurrentPathAndRedirectToLogin(navigate));
  const assertAccount = () => {
    if (!user || String(useUserStore.getState().user?.id) !== String(user.id))
      throw new Error('Account changed');
  };
  const lists = useQuery({
    queryKey: ['album-action-lists', user?.id],
    queryFn: async () => (await meApi.getMyLists()).data ?? [],
    enabled: Boolean(user && mode === 'playlist'),
  });
  const albums = (lists.data ?? []).filter((list) => list.type === 'ALBUM');
  const membership = useQuery({
    queryKey: [
      'album-action-membership',
      user?.id,
      album.albumSource,
      album.sourceId,
      albums.map((p) => `${p.id}:${p.updatedAt}`).join(','),
    ],
    queryFn: async () => {
      const details = await Promise.all(albums.map((p) => playlistApi.getPlaylistById(p.id)));
      return details
        .filter((p) => p.data?.albums?.some((item) => sameAlbumSource(item, album)))
        .map((p) => p.data.id);
    },
    enabled: Boolean(user && mode === 'playlist' && albums.length),
    retry: false,
  });
  const hasPlaylist = Boolean(membership.data?.length);
  const refresh = async () => {
    await invalidateExploreSummary(queryClient);
    await Promise.all(
      [
        'albumDetail',
        'albumReviews',
        'reviewsAlbums',
        'browse-reviews',
        'review',
        'myReviews',
        'me',
        'search',
        'relatedAlbums',
        'album-review-history',
        'userProfile',
      ].map((key) => queryClient.invalidateQueries({ queryKey: [key] })),
    );
  };
  const save = async (draft) => {
    assertAccount();
    await saveAlbumReview(album, review, draft, mode);
    if (!review?.id && mode === 'review')
      trackReviewCreate({ contentId: album.sourceId, contentName: album.title, pageType }, user);
    await refresh();
  };
  const remove = async () => {
    assertAccount();
    await deleteAlbumReview(album, review);
    removeRecent(
      recentScope(user.id),
      (item) => item.kind === 'review' && item.id === `review:${review.id}`,
    );
    await refresh();
  };
  const deleting = useRef(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState(false);
  const confirmDelete = async () => {
    if (deleting.current) return;
    deleting.current = true;
    setDeleteBusy(true);
    setDeleteError(false);
    try {
      await remove();
      setDeleteOpen(false);
    } catch {
      setDeleteError(true);
    } finally {
      deleting.current = false;
      setDeleteBusy(false);
    }
  };
  React.useImperativeHandle(actionRef, () => ({
    compose: () => requireLogin(openReview),
    remove: () =>
      requireLogin(() => {
        if (deleting.current || !review?.id) return;
        setDeleteError(false);
        setDeleteOpen(true);
      }),
  }));
  const bookmark = () =>
    requireLogin(async () => {
      if (bookmarkBusy.current || bookmarkPending) return;
      bookmarkBusy.current = true;
      setBookmarkWorking(true);
      setMessage('');
      setBookmarkOverride(!saved);
      try {
        assertAccount();
        await onBookmarkToggle();
      } catch {
        setMessage(
          en ? 'Could not save. Please try again.' : '저장하지 못했어요. 다시 시도해 주세요.',
        );
      } finally {
        setBookmarkOverride(null);
        bookmarkBusy.current = false;
        setBookmarkWorking(false);
      }
    });
  const share = async () => {
    setMessage('');
    try {
      const artist = album.artist?.name;
      const result = await shareOrCopyUrl({
        url: albumSharePath(album),
        event: { albumId: album.id, surface: 'ALBUM_DETAIL', section: 'SHARE' },
        title: `${album.title}${artist ? ` — ${artist}` : ''}`,
        text: artist ? `${artist} — ${album.title}` : album.title,
      });
      if (result === 'copied') setMessage(en ? 'Link copied.' : '링크를 복사했어요.');
      if (result !== 'cancelled')
        trackShareClick(
          {
            contentType: CONTENT_TYPE.ALBUM,
            contentId: album.sourceId,
            contentName: album.title,
            pageType,
          },
          user,
        );
    } catch {
      setMessage(
        en ? 'Could not share. Please try again.' : '공유하지 못했어요. 다시 시도해 주세요.',
      );
    }
  };
  const choose = (id) => {
    assertAccount();
    setMode(null);
    navigate(id == null ? '/lists/create' : `/lists/${id}/edit`, {
      state: { seedAlbum: albumPlaylistSeed(album), seedUserId: user.id },
    });
  };
  const items = [
    {
      label: rated ? (en ? 'Edit Rating' : '평가 수정') : en ? 'Ratings' : '평가',
      a11y: rated
        ? en
          ? 'Edit album rating'
          : '앨범 평가 수정'
        : en
          ? 'Rate this album'
          : '앨범 평가하기',
      selected: rated,
      icon: <RatingStar outline size={22} value={rated ? 1 : 0} />,
      onClick: () => requireLogin(() => setMode('rating')),
    },
    {
      label: reviewed ? (en ? 'Edit Review' : '리뷰 수정') : en ? 'Review' : '리뷰',
      a11y: reviewed
        ? en
          ? 'Edit my review'
          : '내 리뷰 수정'
        : en
          ? 'Write a review for this album'
          : '이 앨범 리뷰 작성',
      selected: reviewed,
      icon: <FiEdit size={20} />,
      onClick: () => requireLogin(openReview),
    },
    {
      label: en ? 'Save' : '저장',
      a11y: saved ? (en ? 'Remove from Saved Albums' : '저장 해제') : en ? 'Save' : '저장',
      selected: saved,
      icon: <FiBookmark size={20} fill={saved ? 'currentColor' : 'none'} />,
      disabled: bookmarkPending || bookmarkWorking,
      onClick: bookmark,
    },
    {
      label: hasPlaylist
        ? en
          ? 'Edit Playlist'
          : '플리 수정'
        : en
          ? 'Add to Playlist'
          : '플리 추가',
      a11y: hasPlaylist
        ? en
          ? 'Edit album playlist'
          : '앨범 플리 수정'
        : en
          ? 'Add to Playlist'
          : '플리에 추가',
      selected: hasPlaylist,
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M11 12H3M16 6H3M16 18H3M18 9v6M21 12h-6" />
        </svg>
      ),
      onClick: () => requireLogin(() => setMode('playlist')),
    },
    { label: en ? 'Share' : '공유', icon: <FiUpload size={20} />, onClick: share },
  ];
  return (
    <>
      {headerOptions && (
        <AlbumDetailHeader
          album={album}
          saved={saved}
          reviewed={reviewed}
          pending={bookmarkPending || bookmarkWorking}
          onBookmark={bookmark}
          onReview={() => requireLogin(openReview)}
          {...headerOptions}
        />
      )}
      <div
        className="pp-album-primary-actions"
        aria-label={en ? 'Album actions' : '앨범 주요 동작'}
      >
        {items.map((item) => (
          <div className="pp-album-primary-item" key={item.label}>
            <button
              type="button"
              aria-label={item.a11y || item.label}
              aria-pressed={item.selected}
              disabled={item.disabled}
              onClick={item.onClick}
            >
              {item.icon}
            </button>
            <span>{item.label}</span>
          </div>
        ))}
      </div>
      {message && (
        <p className="pp-album-action-message" role="status">
          {message}
        </p>
      )}
      {deleteOpen && (
        <AlbumActionConfirm
          kind="review"
          en={en}
          busy={deleteBusy}
          error={deleteError}
          onCancel={() => {
            if (!deleting.current) setDeleteOpen(false);
          }}
          onConfirm={confirmDelete}
        />
      )}
      {(mode === 'rating' || mode === 'review') && (
        <AlbumActionEditor
          album={album}
          review={review}
          mode={mode}
          inputRef={reviewInput}
          en={en}
          onClose={() => setMode(null)}
          onSave={save}
          onDelete={remove}
        />
      )}
      <StableDialog
        open={mode === 'playlist'}
        onClose={() => setMode(null)}
        className="pp-album-dialog"
        fullWidth
        maxWidth="sm"
        aria-labelledby="album-playlist-title"
      >
        <DialogTitle id="album-playlist-title" className="pp-album-dialog-title">
          {en ? 'Add to Playlist' : '플리에 추가'}
          <button type="button" onClick={() => setMode(null)} aria-label={en ? 'Close' : '닫기'}>
            <FiX size={20} />
          </button>
        </DialogTitle>
        <DialogContent className="pp-album-playlist-picker">
          <button type="button" onClick={() => choose(null)}>
            <FiPlus size={20} />
            <span>{en ? 'Create New Album Playlist' : '새 앨범 플리 만들기'}</span>
            <FiChevronRight size={20} />
          </button>
          {lists.isLoading && <p role="status">{en ? 'Loading…' : '불러오는 중…'}</p>}
          {lists.isError && (
            <div role="alert">
              {en ? 'Could not load playlists.' : '플리를 불러오지 못했어요.'}
              <button onClick={() => lists.refetch()}>{en ? 'Retry' : '다시 시도'}</button>
            </div>
          )}
          {lists.isSuccess && !albums.length && (
            <p>
              {en ? 'No album playlists are available.' : '추가할 수 있는 앨범 플리가 없습니다.'}
            </p>
          )}
          {membership.isError && (
            <p role="status">
              {en ? 'Could not check album membership.' : '앨범 포함 여부를 확인하지 못했어요.'}
            </p>
          )}
          {albums.map((list) => (
            <button type="button" key={list.id} onClick={() => choose(list.id)}>
              <FiList size={20} />
              <span>
                {list.title}
                <small>
                  {en ? `${list.albumCount ?? 0} albums` : `앨범 ${list.albumCount ?? 0}개`}
                </small>
              </span>
              <FiChevronRight size={20} />
            </button>
          ))}
        </DialogContent>
      </StableDialog>
    </>
  );
}
